import { Howl, Howler } from 'howler';
import { useEffect, useReducer } from 'react';
/**
 * Métadonnées par son. URL implicite = `/sounds/<name>.mp3`. Volume permet
 * de calibrer (les sons fournis sont rarement uniformes).
 */
const SOUND_META = {
    'ui-click': { volume: 0.3 },
    'ui-hover': { volume: 0.15 },
    'success-ding': { volume: 0.5 },
    'fail-buzzer': { volume: 0.4 },
    'crowd-cheer': { volume: 0.4 },
    'crowd-gasp': { volume: 0.4 },
    'countdown-tick': { volume: 0.5 },
    'countdown-go': { volume: 0.6 },
    'pool-ambient': { volume: 0.25, loop: true },
    'gallop-loop': { volume: 0.3, loop: true },
    'wind-loop': { volume: 0.3, loop: true },
    'ocean-loop': { volume: 0.3, loop: true },
    'boxing-crowd': { volume: 0.3, loop: true },
    'rope-tension': { volume: 0.2, loop: true },
    'fencing-music': { volume: 0.25, loop: true },
    'simon-drum-loop': { volume: 0.3, loop: true },
    'heartbeat': { volume: 0.4, loop: true },
    'quiz-suspense': { volume: 0.35, loop: true },
};
// ────────────────────────────────────────────────────────────────────────────
// État global persisté
// ────────────────────────────────────────────────────────────────────────────
const STORAGE_ENABLED = 'alfa.audio.enabled';
const STORAGE_VOLUME = 'alfa.audio.volume';
function loadBool(key, defaultValue) {
    try {
        const v = localStorage.getItem(key);
        if (v === null)
            return defaultValue;
        return v === 'true';
    }
    catch {
        return defaultValue;
    }
}
function loadNumber(key, defaultValue) {
    try {
        const v = localStorage.getItem(key);
        if (v === null)
            return defaultValue;
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : defaultValue;
    }
    catch {
        return defaultValue;
    }
}
let _enabled = loadBool(STORAGE_ENABLED, true);
let _volume = loadNumber(STORAGE_VOLUME, 0.6);
// Apply initial volume to Howler global (Howler.mute applied via setEnabled below)
Howler.volume(_volume);
Howler.mute(!_enabled);
const listeners = new Set();
function notify() {
    for (const l of listeners)
        l();
}
export function isAudioEnabled() {
    return _enabled;
}
export function getMasterVolume() {
    return _volume;
}
export function setAudioEnabled(v) {
    _enabled = v;
    try {
        localStorage.setItem(STORAGE_ENABLED, String(v));
    }
    catch { /* ignore */ }
    Howler.mute(!v);
    notify();
}
export function setMasterVolume(v) {
    _volume = Math.max(0, Math.min(1, v));
    try {
        localStorage.setItem(STORAGE_VOLUME, String(_volume));
    }
    catch { /* ignore */ }
    Howler.volume(_volume);
    notify();
}
// ────────────────────────────────────────────────────────────────────────────
// Cache + lazy load des Howl instances
// ────────────────────────────────────────────────────────────────────────────
const cache = new Map();
function getHowl(name) {
    let h = cache.get(name);
    if (h)
        return h;
    const meta = SOUND_META[name] ?? {};
    h = new Howl({
        src: [`/sounds/${name}.mp3`, `/sounds/${name}.ogg`],
        volume: meta.volume ?? 1,
        loop: meta.loop ?? false,
        preload: false, // chargé à la première lecture
        html5: meta.loop ?? false, // streaming pour les loops, mémoire pour les courts
        onloaderror: (_id, err) => {
            if (import.meta.env.DEV) {
                // eslint-disable-next-line no-console
                console.warn(`[audio] missing sound "${name}" — drop a file at /sounds/${name}.mp3`, err);
            }
        },
    });
    cache.set(name, h);
    return h;
}
// ────────────────────────────────────────────────────────────────────────────
// API publique pour jouer / boucler / stopper
// ────────────────────────────────────────────────────────────────────────────
/** Joue un son une fois. No-op si audio désactivé OU fichier manquant. */
export function playSound(name) {
    if (!_enabled)
        return;
    try {
        getHowl(name).play();
    }
    catch {
        /* silently ignore */
    }
}
/**
 * Lance un son en boucle (idempotent — si déjà en cours, retourne le même
 * Howl). Retourne une fonction de cleanup qui fade out + stop.
 */
export function playLoop(name, opts) {
    const fadeIn = opts?.fadeInMs ?? 800;
    const fadeOut = opts?.fadeOutMs ?? 500;
    const h = getHowl(name);
    const meta = SOUND_META[name] ?? {};
    const targetVol = meta.volume ?? 0.5;
    if (!h.playing()) {
        h.volume(0);
        h.play();
        h.fade(0, _enabled ? targetVol : 0, fadeIn);
    }
    return () => {
        if (!h.playing())
            return;
        h.fade(h.volume(), 0, fadeOut);
        window.setTimeout(() => h.stop(), fadeOut + 50);
    };
}
/** Stoppe immédiatement un son (utile pour les loops). */
export function stopSound(name) {
    const h = cache.get(name);
    if (h)
        h.stop();
}
// ────────────────────────────────────────────────────────────────────────────
// Hook React pour les composants qui ont besoin de re-render sur changement
// ────────────────────────────────────────────────────────────────────────────
export function useAudioSettings() {
    const [, force] = useReducer((c) => c + 1, 0);
    useEffect(() => {
        listeners.add(force);
        return () => { listeners.delete(force); };
    }, []);
    return {
        enabled: _enabled,
        volume: _volume,
        setEnabled: setAudioEnabled,
        setVolume: setMasterVolume,
    };
}
